/* Size Guide — custom element.
   Phase 1: tabs.  Phase 2: Inches/Cms toggle.  Phase 3: size-row select + add-to-bag.
   Later: model (i) tooltips. */
if (!customElements.get('size-guide')) {
  class SizeGuide extends HTMLElement {
    connectedCallback() {
      // Tabs
      this.tabs = Array.from(this.querySelectorAll('[data-sg-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-sg-panel]'));
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.activate(tab.dataset.sgTab)));

      // Unit toggle
      const table = this.querySelector('[data-base-unit]');
      this.baseUnit = table ? table.dataset.baseUnit : 'in';
      this.cells = Array.from(this.querySelectorAll('td[data-base]'));
      this.unitBtns = Array.from(this.querySelectorAll('[data-sg-unit]'));
      this.unitBtns.forEach((btn) => btn.addEventListener('click', () => this.setUnit(btn.dataset.sgUnit)));

      // Size selection + add to bag
      this.rows = Array.from(this.querySelectorAll('.size-guide__row[data-variant-id]'));
      this.variantInput = this.querySelector('[data-sg-variant-input]');
      this.addButton = this.querySelector('.size-guide__footer [type="submit"]');
      this.form = this.querySelector('.size-guide__footer form');

      this.rows.forEach((row) => {
        row.addEventListener('click', (e) => {
          // Let the model-fit (i) open its tooltip without also selecting the row
          if (e.target.closest('.model-hint')) return;
          this.selectRow(row);
        });
        row.addEventListener('keydown', (e) => {
          if (e.target.closest('.model-hint')) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectRow(row);
          }
        });
      });

      // Fast add-to-bag: one request (add + bundled cart-drawer section), open the drawer straight
      // from that response — skips the theme's extra /cart.js round-trip. The native product-form
      // submit stays as a fallback if this ever throws.
      if (this.addButton) {
        this.addButton.addEventListener('click', (e) => {
          if (!this.variantInput || !this.variantInput.value) return;
          e.preventDefault();
          this.addToCart();
        });
      }
    }

    activate(name) {
      this.tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.sgTab === name));
      this.panels.forEach((p) => p.classList.toggle('is-active', p.dataset.sgPanel === name));
    }

    setUnit(unit) {
      this.unitBtns.forEach((b) => b.classList.toggle('is-active', b.dataset.sgUnit === unit));
      this.cells.forEach((td) => {
        const raw = td.dataset.base;
        const base = parseFloat(raw);
        if (isNaN(base) || unit === this.baseUnit) {
          td.textContent = raw;
          return;
        }
        const converted = this.baseUnit === 'in' ? base * 2.54 : base / 2.54;
        td.textContent = (Math.round(converted * 10) / 10).toString();
      });
    }

    selectRow(row) {
      this.rows.forEach((r) => {
        const selected = r === row;
        r.classList.toggle('is-selected', selected);
        r.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      if (this.variantInput) this.variantInput.value = row.dataset.variantId;
      if (this.addButton) this.addButton.disabled = false;
    }

    addToCart() {
      if (this.adding) return;
      const id = this.variantInput && this.variantInput.value;
      if (!id) return;
      this.adding = true;
      this.setBusy(true);

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      // Ask the theme which sections to re-render — the cart drawer registers its own section id here.
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
      );

      const body = new FormData();
      body.append('id', id);
      body.append('quantity', '1');
      if (sections.length) {
        body.append('sections', sections.join(','));
        body.append('sections_url', root + 'variants/' + id);
      }

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: body,
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          this.adding = false;
          this.setBusy(false);

          if (!ok) {
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:error', { bubbles: true, detail: { error: data.description || data.message } })
            );
            return;
          }

          this.closeModal();

          // Open + refresh the drawer straight from the rendered section. Header count is optimistic
          // (drawer contents come from the section HTML, so they're always accurate).
          const countEl = document.querySelector('cart-count');
          const prev = countEl ? parseInt(countEl.textContent, 10) || 0 : 0;

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'variant:add',
                cart: { item_count: prev + 1, sections: data.sections || {} },
              },
            })
          );
        })
        .catch(() => {
          this.adding = false;
          this.setBusy(false);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        });
    }

    setBusy(busy) {
      if (!this.addButton) return;
      if (busy) this.addButton.setAttribute('aria-busy', 'true');
      else this.addButton.removeAttribute('aria-busy');
      this.addButton.disabled = busy;
    }

    closeModal() {
      const modal = this.closest('x-modal');
      if (!modal) return;
      if (typeof modal.hide === 'function') modal.hide();
      else modal.removeAttribute('open');
    }
  }

  customElements.define('size-guide', SizeGuide);
}
